import { v4 as uuid } from "uuid";
import { useEffect } from "react";
import config from "config/config";
import { useForm } from "react-hook-form";

import FormInputError from "components/General/FormInputError";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import { useTranslation } from "react-i18next";
import { getImage, isAgentPortal } from "js/utilities";
import { getUnauthAgentPrechatInfo } from "./utils/getUnauthAgentPrechatInfo";
import { getUnauthCustomerPrechatInfo } from "./utils/getUnauthCustomerPrechatInfo";

export default function ChatUnauthFormWindow({
  setHideCustomChat,
  connectingToSF,
  setConnectingToSF,
  openChatWindow,
  setOpenChatWindow,
}) {
  const UUID = uuid();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const initESW = ({ getValueByLabel, routingQueueName }) => {
    window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;
    window.embeddedservice_bootstrap.settings.language = "en_US";
    if (config.SFDC_VERSION === "PROD") {
      window.embeddedservice_bootstrap.init(
        "00Df400000488Mv",
        "EVL_Chat_Support",
        "https://nttfsiclaims.my.site.com/ESWEVLChatSupport1770287966136",
        {
          scrt2URL: "https://nttfsiclaims.my.salesforce-scrt.com",
        }
      );
    } else if (config.SFDC_VERSION === "UAT") {
      // UAT
      window.embeddedservice_bootstrap.init(
        "00DcZ000000rXkE",
        "EVL_Chat_Support",
        "https://nttfsiclaims--nttfsiuat.sandbox.my.site.com/ESWEVLChatSupport1769583709310",
        {
          scrt2URL:
            "https://nttfsiclaims--nttfsiuat.sandbox.my.salesforce-scrt.com",
        }
      );
    } else if (config.SFDC_VERSION === "MO") {
      // MO
      window.embeddedservice_bootstrap.init(
        "00DTI000004vKir",
        "EVL_Chat_Agent_Channel_New",
        "https://nttfsiclaims--fsitagqa.sandbox.my.site.com/ESWEVLChatAgentChannel1768226837707",
        {
          scrt2URL:
            "https://nttfsiclaims--fsitagqa.sandbox.my.salesforce-scrt.com",
        }
      );
    }

    window.addEventListener("onEmbeddedMessagingReady", () => {
      window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
        Description: "",
        CarrierCode: getValueByLabel("Carrier Code") || "",
        CarrierName: getValueByLabel("Carrier Name") || "",
        Caller_Type: getValueByLabel("Caller type") || "",
        Category: "",
        Issue_Type: "",
        Policy: "",
        AgentId: isAgentPortal() ? getValueByLabel("Agent ID") || "" : "",
        AlternateAgentId: isAgentPortal()
          ? getValueByLabel("Alt Agent ID") || ""
          : "",
        Agent_Email: isAgentPortal()
          ? getValueByLabel("Agent Email") || ""
          : getValueByLabel("Contact Email") || "",
        Caller_Name: getValueByLabel("Caller Name") || "",
        Old_Policy: "",
        Portal_Chat_Id: getValueByLabel("Portal Chat ID") || "",
        Line_Of_Business: "",
        RoutingQueueName: routingQueueName || "",
      });
    });

    window.addEventListener(
      "onEmbeddedMessagingConversationStarted",
      () => {
        setHideCustomChat(true);
        setConnectingToSF(false);
      },
      { once: true }
    );

    window.addEventListener("onEmbeddedMessagingButtonCreated", () => {
      window.embeddedservice_bootstrap.utilAPI.launchChat();
      setHideCustomChat(true);
      setConnectingToSF(false);
      setOpenChatWindow(false);
    });

    window.addEventListener(
      "onEmbeddedMessagingConversationClosed",
      () => {
        if (config.ENABLE_POST_CHAT_SURVEY === "true") {
          window.open(`/survey?uuid=${UUID}`);
        }
        setHideCustomChat(false);
        setConnectingToSF(false);
        setOpenChatWindow(false);
        window.location.reload();
      },
      { once: true }
    );
  };

  const connectSFDCForLoginHelp = async (data) => {
    setConnectingToSF(true);

    const prechatInfo = isAgentPortal()
      ? getUnauthAgentPrechatInfo(UUID, data)
      : getUnauthCustomerPrechatInfo(UUID, data);

    const getValueByLabel = (label) =>
      prechatInfo?.extraPrechatFormDetails?.find((item) => item.label === label)
        ?.value || "";

    const routingQueueName = isAgentPortal()
      ? "EVL_Technical_Assistance_Agent"
      : "EVL_Technical_Assistance_Customer";

    if (!window.embeddedservice_bootstrap) {
      const s = document.createElement("script");
      s.src =
        config.SFDC_VERSION === "PROD"
          ? "https://nttfsiclaims.my.site.com/ESWEVLChatSupport1770287966136/assets/js/bootstrap.min.js"
          : config.SFDC_VERSION === "UAT"
          ? "https://nttfsiclaims--nttfsiuat.sandbox.my.site.com/ESWEVLChatSupport1769583709310/assets/js/bootstrap.min.js"
          : "https://nttfsiclaims--fsitagqa.sandbox.my.site.com/ESWEVLChatAgentChannel1768226837707/assets/js/bootstrap.min.js";

      s.onload = () =>
        initESW({
          getValueByLabel,
          routingQueueName,
        });

      document.body.appendChild(s);
    } else {
      initESW({
        getValueByLabel,
        routingQueueName,
      });
    }
  };

  useEffect(() => {
    reset();
  }, [openChatWindow]);

  return (
    <div className="ui-card fixed bottom-2 right-6 block shadow-sm rounded-lg z-[500] w-[320px]">
      <EverlakeLoadingOverlay
        isLoading={connectingToSF}
        loadingText={t("UnAuthLoginRegisterHelp-loading-text")}
      >
        <div className=" h-[50px] bg-ui-blue text-white flex justify-between items-center text-lg rounded-t-lg">
          <div className="flex flex-row px-4">
            {config.COMPANY_NAME === "everlake" && (
              <img
                className="w-7 h-7 mr-2"
                src={getImage("symbol_white.png")}
                alt={t("UnAuthLoginRegisterHelp-alt-text")}
              />
            )}

            <h3 className="font-semibold">
              {t("UnAuthLoginRegisterHelp-title")}
            </h3>
          </div>
          <div className="px-2">
            <button
              className="hover:bg-white/30 hover:rounded-lg p-2"
              aria-label={t("UnAuthLoginRegisterHelp-aria-label-1")}
              onClick={() => setOpenChatWindow(false)}
            >
              <svg
                className="ui-icon h-5 w-5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-minus"></use>
              </svg>
            </button>
            <button
              className="hover:bg-white/30 hover:rounded-lg p-2 ml-0"
              aria-label={t("UnAuthLoginRegisterHelp-aria-label-2")}
              onClick={() => setOpenChatWindow(false)}
            >
              <svg
                className="ui-icon h-5 w-5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
          </div>
        </div>
        <div className="flex flex-col py-5 px-8 overflow-auto h-[460px] text-left">
          <h3 className="text-center text-ui-blue font-semibold text-md">
            {t("UnAuthLoginRegisterHelp-subTitle")}
          </h3>
          <div className="flex">
            <hr className="w-[45%] mt-4 mb-4" />
            <svg
              className="ui-icon h-5 w-[10%] self-center text-ui-blue-dark"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-chat-bubble"></use>
            </svg>
            <hr className="w-[45%] mt-4 mb-4" />
          </div>

          <p className="text-sm">{t("UnAuthLoginRegisterHelp-description")}</p>

          <form
            onSubmit={handleSubmit(connectSFDCForLoginHelp)}
            className="flex flex-col justify-between h-full mt-4"
          >
            <div className="flex flex-col gap-y-4">
              <div>
                <label htmlFor="login-help-name" className="block mb-1">
                  {t("UnAuthLoginRegisterHelp-label-1")}
                </label>
                <input
                  id="login-help-name"
                  {...register("name", {
                    required: t("UnAuthLoginRegisterHelp-error-1"),
                  })}
                />
                <FormInputError
                  id="login-help-name-error"
                  errorMessage={errors?.name?.message}
                />
              </div>

              <div>
                <label htmlFor="login-help-email" className="block mb-1">
                  {t("UnAuthLoginRegisterHelp-label-2")}
                </label>
                <input
                  id="login-help-email"
                  {...register("email", {
                    required: t("UnAuthLoginRegisterHelp-error-2"),
                    pattern: {
                      value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                      message: t("UnAuthLoginRegisterHelp-error-3"),
                    },
                  })}
                />
                <FormInputError
                  id="login-help-email-error"
                  errorMessage={errors?.email?.message}
                />
              </div>

              {isAgentPortal() && (
                <div>
                  <label htmlFor="login-help-agent-id" className="block mb-1">
                    {t("UnAuthLoginRegisterHelp-label-3")}
                  </label>
                  <input id="login-help-agent-id" {...register("agentId")} />
                </div>
              )}
            </div>
            <div className="flex flex-row justify-between gap-x-4 mt-3">
              <button
                className="ui-btn-primary w-full h-10"
                type="button"
                onClick={() => setOpenChatWindow(false)}
              >
                {t("UnAuthLoginRegisterHelp-button-1")}
              </button>
              <button className="ui-btn-primary w-full h-10" type="submit">
                {t("UnAuthLoginRegisterHelp-button-2")}
              </button>
            </div>
            <p className="text-xs mt-[5px]">
              {t("UnAuthLoginRegisterHelp-note-1")}
            </p>
          </form>
        </div>
      </EverlakeLoadingOverlay>
    </div>
  );
}
